import * as React from 'react';
import { useMemo } from 'react';
import {
  createBrowserRouter,
  RouteObject,
  RouterProvider,
  Outlet,
  Navigate,
} from 'react-router-dom';

import { ClaimForm } from './component/Claim/ClaimForm';
import { NotFound } from './component/NotFound';
import { ProtectedRoute } from './component/ProtectedRoute';
import { Unauthorized } from './component/Unauthorized';
import { UnprotectedRoute } from './component/UnprotectedRoute';
import { IS_PROD } from './config';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ValidRoutePathsProvider } from './context/ValidRoutePathsContext';
import { AncillaryProductInfo } from './pages/AncillaryProductInfo';
import { AstraVerification } from './pages/AstraVerification';
import { CommandRunnerDashboard } from './pages/CommandRunnerDashboard';
import { CreateDiscount } from './pages/CreateDiscount';
import { CreateFunnelPromotion } from './pages/CreateFunnelPromotion';
import { CreateItem } from './pages/CreateItem';
import { CreatePortalPromotion } from './pages/CreatePortalPromotion';
import { CreatePostCheckoutSurveyOptionPage } from './pages/CreatePostCheckoutSurveyOptionPage';
import { CreateUser } from './pages/CreateUser';
import { CustomerChangelog } from './pages/CustomerChangelog';
import { CustomerLookup } from './pages/CustomerLookup';
import { CustomerProfile } from './pages/CustomerProfile';
import { CustomerRefund } from './pages/CustomerRefund';
import { DiscountLookup } from './pages/DiscountLookup';
import { Discounts } from './pages/Discounts';
import { EditPostCheckoutSurveyOptionPage } from './pages/EditPostCheckoutSurveyOptionPage';
import { FetchAi } from './pages/FetchAi';
import { FetchAiUpdates } from './pages/FetchAiUpdates';
import { ForgotPassword } from './pages/ForgotPassword';
import { FreshProductInfo } from './pages/FreshProductInfo';
import { Home } from './pages/Home';
import { Items } from './pages/Items';
import { LeadLookup } from './pages/LeadLookup';
import { LeadProfile } from './pages/LeadProfile';
import { Login } from './pages/Login';
import ManageData from './pages/ManageData';
import { NutrientAnalysis } from './pages/NutrientAnalysis';
import { OnCallDashboard } from './pages/OnCallDashboard';
import { OrderDetailFull } from './pages/OrderDetailFull';
import { PostCheckoutSurveyChangelog } from './pages/PostCheckoutSurveyChangelog';
import { PostCheckoutSurveyOptions } from './pages/PostCheckoutSurveyOptions';
import { ProductItems } from './pages/ProductItems';
import ProductItemsMappingForm from './pages/ProductItemsMappingForm';
import { ProductPricingNutrientInfo } from './pages/ProductPricingNutrientInfo';
import { Promotions } from './pages/Promotions';
import { ResetPassword } from './pages/ResetPassword';
import SelectPromotionType from './pages/SelectPromotionType';
import { SplitFulfillment } from './pages/SplitFulfillment';
import { Category, SystemChangelog } from './pages/SystemChangelog';
import { UnKibbleProductInfo } from './pages/UnKibbleProductInfo';
import { UpdateDiscountContainer } from './pages/UpdateDiscountContainer';
import { UserManagement } from './pages/UserManagement';
import { ViewPromotion } from './pages/ViewPromotion';
import {
  isAdmin,
  isOpsAdmin,
  isTeamMember,
  isAllModuleAccessUser,
  isMarketingAdmin,
} from './pages/constants';
import { CountryCode } from './pages/types';

const Layout = (
  <div>
    <Outlet />
  </div>
);

/**
 * Recursively collects all path patterns from a route config array.
 * Skips the catch-all '*' path since it's not a valid redirect target.
 */
const collectRoutePaths = (routes: RouteObject[]): string[] =>
  routes.flatMap((route) => [
    ...(route.path && route.path !== '*' ? [route.path] : []),
    ...(route.children ? collectRoutePaths(route.children) : []),
  ]);

export const BarkRouter = () => {
  const { isAuthenticated } = useAuth();

  const routeConfig: RouteObject[] = [
    {
      path: '*',
      element: <NotFound />,
    },
    {
      path: '/login',
      element: (
        <UnprotectedRoute>
          <Login />
        </UnprotectedRoute>
      ),
    },
    {
      path: '/resetpassword',
      element: <ForgotPassword />,
    },
    {
      path: '/reset-password/:tokenId/:tokenKey',
      element: <ResetPassword />,
    },
    {
      path: '/astra-1d7235e0-24ad-499f-ae7c-4182657afd9c.html',
      element: <AstraVerification />,
    },
    {
      path: '/',
      element: <ProtectedRoute />,
      handle: {
        title: 'Home',
      },
      children: [
        {
          path: '/',
          element:
            isAuthenticated &&
            (isAdmin() ||
              isTeamMember() ||
              isOpsAdmin() ||
              isMarketingAdmin() ||
              isAllModuleAccessUser()) ? (
              <Home />
            ) : (
              <Unauthorized />
            ),
        },
        {
          path: '/user-management',
          element:
            isAuthenticated && (isAdmin() || isOpsAdmin() || isAllModuleAccessUser()) ? (
              Layout
            ) : (
              <Unauthorized />
            ),
          handle: {
            title: 'Manage Users',
          },
          children: [
            {
              path: '/user-management',
              element: <UserManagement />,
            },
            {
              path: '/user-management/create-user',
              element: <CreateUser />,
              handle: {
                title: 'Create user',
              },
            },
          ],
        },
        {
          path: '/create-user',
          element: <CreateUser />,
          handle: {
            title: 'Create user',
          },
        },
        {
          path: '/customer-lookup',
          element: Layout,
          handle: {
            title: 'Customer Lookup',
          },
          children: [
            {
              path: '/customer-lookup',
              element: <CustomerLookup />,
            },
            {
              path: '/customer-lookup/:customerId',
              element: Layout,
              handle: {
                title: 'customerId',
              },
              children: [
                {
                  path: '/customer-lookup/:customerId',
                  element: <CustomerProfile />,
                },
                {
                  path: '/customer-lookup/:customerId/changelog',
                  element: <CustomerChangelog />,
                  handle: {
                    title: 'Changelog',
                  },
                },
                {
                  path: '/customer-lookup/:customerId/order/:orderId',
                  element: Layout,
                  handle: {
                    title: 'orderId',
                  },
                  children: [
                    {
                      path: '/customer-lookup/:customerId/order/:orderId',
                      element: <OrderDetailFull />,
                    },
                    {
                      path: '/customer-lookup/:customerId/order/:orderId/refund',
                      element: <CustomerRefund />,
                      handle: {
                        title: 'Refund Order',
                      },
                    },
                    {
                      path: '/customer-lookup/:customerId/order/:orderId/fulfillment/:fulfillmentId/pet/:petId/product/:productCode/claim',
                      element:
                        isAuthenticated &&
                        (isTeamMember() || isAdmin() || isOpsAdmin() || isAllModuleAccessUser()) ? (
                          <ClaimForm />
                        ) : (
                          <Unauthorized />
                        ),
                      handle: {
                        title: 'Create Claim',
                      },
                    },
                    {
                      path: '/customer-lookup/:customerId/order/:orderId/fulfillment/:fulfillmentId/pet/:petId/product/:productCode/claim/:claimId',
                      element:
                        isAuthenticated &&
                        (isTeamMember() || isAdmin() || isOpsAdmin() || isAllModuleAccessUser()) ? (
                          <ClaimForm />
                        ) : (
                          <Unauthorized />
                        ),
                      handle: {
                        title: 'Edit Claim',
                      },
                    },
                    {
                      path: '/customer-lookup/:customerId/order/:orderId/:fulfillmentId/split-fulfillment',
                      element: <SplitFulfillment />,
                      handle: {
                        title: 'Split Fulfillment',
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/lead-lookup',
          element: Layout,
          handle: {
            title: 'Lead Lookup',
          },
          children: [
            {
              path: '/lead-lookup',
              element: <LeadLookup />,
            },
            {
              path: '/lead-lookup/:leadId',
              element: Layout,
              handle: {
                title: 'leadId',
              },
              children: [
                {
                  path: '/lead-lookup/:leadId',
                  element: <LeadProfile />,
                },
              ],
            },
          ],
        },
        {
          path: '/discount-lookup',
          element: <DiscountLookup />,
          handle: {
            title: 'Discount Lookup',
          },
          children: [
            {
              path: '/discount-lookup/:discountId',
              element: <DiscountLookup />,
              handle: {
                title: 'View Discount',
              },
            },
          ],
        },
        {
          path: '/discounts',
          element:
            isAuthenticated &&
            (isAdmin() || isOpsAdmin() || isMarketingAdmin() || isAllModuleAccessUser()) ? (
              Layout
            ) : (
              <Unauthorized />
            ),
          handle: {
            title: 'Manage Discounts',
          },
          children: [
            {
              path: '/discounts',
              element: <Discounts />,
            },
            {
              path: '/discounts/create-discount',
              element: <CreateDiscount />,
              handle: {
                title: 'Create Discount',
              },
            },
            {
              path: '/discounts/:discountId',
              element: <UpdateDiscountContainer />,
              handle: {
                title: 'Update Discount',
              },
            },
            {
              path: '/discounts/changelog',
              element: <SystemChangelog category={Category.Discounts} />,
              handle: {
                title: 'Changelog',
              },
            },
          ],
        },
        {
          path: '/post-checkout-survey-options',
          element:
            isAuthenticated && (isMarketingAdmin() || isAllModuleAccessUser()) ? (
              Layout
            ) : (
              <Unauthorized />
            ),
          handle: {
            title: 'Manage Post-Purchase Survey',
          },
          children: [
            {
              element: Layout,
              handle: {
                title: 'How did you hear about Spot & Tango?',
              },
              children: [
                {
                  index: true,
                  element: <PostCheckoutSurveyOptions />,
                },
                {
                  path: 'create',
                  element: <CreatePostCheckoutSurveyOptionPage />,
                  handle: {
                    title: 'Create New Option',
                  },
                },
                {
                  path: ':id/edit',
                  element: <EditPostCheckoutSurveyOptionPage />,
                  handle: {
                    title: 'Edit Option',
                  },
                },
                {
                  path: 'changelog',
                  element: <PostCheckoutSurveyChangelog />,
                  handle: {
                    title: 'Changelog',
                  },
                },
              ],
            },
          ],
        },
        {
          path: '/promotions',
          element: isAuthenticated && isAllModuleAccessUser() ? Layout : <Unauthorized />,
          handle: {
            title: 'Manage Promotions',
          },
          children: [
            {
              path: '/promotions',
              element: <Promotions />,
            },
            {
              path: '/promotions/select-promotion-surface',
              element: <SelectPromotionType />,
              handle: {
                title: 'Select Promotion Surface',
              },
            },
            {
              path: '/promotions/create-funnel-promotion',
              element: <CreateFunnelPromotion />,
              handle: {
                title: 'Create Funnel Promotion',
              },
            },
            {
              path: '/promotions/create-portal-promotion',
              element: <CreatePortalPromotion />,
              handle: {
                title: 'Create Portal Promotion',
              },
            },
            {
              path: '/promotions/:promotionId',
              element: <ViewPromotion />,
              handle: {
                title: 'View Promotion',
              },
            },
          ],
        },
        {
          path: '/fetch-ai',
          element:
            isAuthenticated && isAllModuleAccessUser() && !IS_PROD ? Layout : <Unauthorized />,
          handle: {
            title: 'FETCH AI',
          },
          children: [
            {
              path: '/fetch-ai',
              element: <FetchAi />,
            },
            {
              path: '/fetch-ai/:agentId',
              element: <FetchAiUpdates />,
              handle: {
                title: 'FETCH AI Updates',
              },
            },
          ],
        },
        {
          path: '/on-call-dashboard',
          element: isAuthenticated && isAllModuleAccessUser() ? Layout : <Unauthorized />,
          handle: {
            title: 'On-Call Dashboard',
          },
          children: [
            {
              path: '/on-call-dashboard',
              element: <OnCallDashboard />,
            },
          ],
        },
        {
          path: '/command-runner',
          element: isAuthenticated && isAllModuleAccessUser() ? Layout : <Unauthorized />,
          handle: {
            title: 'Command Runner',
          },
          children: [
            {
              path: '/command-runner',
              element: <CommandRunnerDashboard />,
            },
            {
              path: '/command-runner/changelog',
              element: <SystemChangelog category={Category.CommandRunner} />,
              handle: {
                title: 'Changelog',
              },
            },
          ],
        },
        {
          path: '/manage-data',
          element:
            isAuthenticated && (isOpsAdmin() || isAllModuleAccessUser()) ? (
              Layout
            ) : (
              <Unauthorized />
            ),
          handle: {
            title: 'Manage Products & Items',
          },
          children: [
            {
              path: '/manage-data',
              element: <ManageData />,
            },
            {
              path: '/manage-data/product-items-us',
              element: Layout,
              handle: {
                title: '🇺🇸 US Product-Items',
              },
              children: [
                {
                  path: '/manage-data/product-items-us',
                  element: <ProductItems country={CountryCode.US} />,
                },
                {
                  path: '/manage-data/product-items-us/create-product-items',
                  element: <ProductItemsMappingForm country={CountryCode.US} />,
                  handle: {
                    title: 'Create Product-Item Mapping',
                  },
                },
                {
                  path: '/manage-data/product-items-us/update-product-items/:productId',
                  element: <ProductItemsMappingForm country={CountryCode.US} />,
                  handle: {
                    title: 'Update Product-Item Mapping',
                  },
                },
                {
                  path: '/manage-data/product-items-us/changelog',
                  element: <SystemChangelog category={Category.ProductItems} />,
                  handle: {
                    title: 'Changelog',
                  },
                },
              ],
            },
            {
              path: '/manage-data/product-items-ca',
              element: Layout,
              handle: {
                title: '🇨🇦 CA Product-Items',
              },
              children: [
                {
                  path: '/manage-data/product-items-ca',
                  element: <ProductItems country={CountryCode.CA} />,
                },
                {
                  path: '/manage-data/product-items-ca/create-product-items',
                  element: <ProductItemsMappingForm country={CountryCode.CA} />,
                  handle: {
                    title: 'Create Product-Item Mapping',
                  },
                },
                {
                  path: '/manage-data/product-items-ca/update-product-items/:productId',
                  element: <ProductItemsMappingForm country={CountryCode.CA} />,
                  handle: {
                    title: 'Update Product-Item Mapping',
                  },
                },
                {
                  path: '/manage-data/product-items-ca/changelog',
                  element: <SystemChangelog category={Category.ProductItems} />,
                  handle: {
                    title: 'Changelog',
                  },
                },
              ],
            },
          ],
        },
        {
          path: '/product-items',
          element: <Navigate to="/manage-data/product-items-us" replace />,
        },
        {
          path: '/items',
          element:
            isAuthenticated && (isOpsAdmin() || isAllModuleAccessUser()) ? (
              Layout
            ) : (
              <Unauthorized />
            ),
          handle: {
            title: 'Manage Items',
          },
          children: [
            {
              path: '/items',
              element: <Items />,
            },
            {
              path: '/items/create-item',
              element: <CreateItem />,
              handle: {
                title: 'Create Item',
              },
            },
            {
              path: '/items/changelog',
              element: <SystemChangelog category={Category.Items} />,
              handle: {
                title: 'Changelog',
              },
            },
          ],
        },
        {
          path: '/product-pricing-nutrient-info',
          element: isAuthenticated ? Layout : <Unauthorized />,
          handle: {
            title: 'Product, Pricing, Nutrient Info',
          },
          children: [
            {
              path: '/product-pricing-nutrient-info',
              element: <ProductPricingNutrientInfo />,
            },
            {
              path: '/product-pricing-nutrient-info/unKibble',
              element: <UnKibbleProductInfo />,
              handle: {
                title: 'UnKibble',
              },
            },
            {
              path: '/product-pricing-nutrient-info/fresh',
              element: <FreshProductInfo />,
              handle: {
                title: 'Fresh',
              },
            },
            {
              path: '/product-pricing-nutrient-info/ancillary',
              element: <AncillaryProductInfo />,
              handle: {
                title: 'Ancillary',
              },
            },
            {
              path: '/product-pricing-nutrient-info/nutrient-analysis',
              element: <NutrientAnalysis />,
              handle: {
                title: 'Nutrient Analysis',
              },
            },
          ],
        },
      ],
    },
  ];

  // Route paths are static — only the elements depend on auth state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validPaths = useMemo(() => collectRoutePaths(routeConfig), []);
  const router = createBrowserRouter(routeConfig);

  return (
    <ValidRoutePathsProvider value={validPaths}>
      <RouterProvider router={router} />
    </ValidRoutePathsProvider>
  );
};

const BarkApp = () => (
  <AuthProvider>
    <BarkRouter />
  </AuthProvider>
);

export default BarkApp;
